import type {
  DocumentSessionClientUpdate,
  DocumentProjectionDescriptor,
  DocumentSessionCheckpoint,
  DocumentSessionLease,
  SheetChunkDescriptor,
} from '../protocol/documentSessionProtocol'
import {
  DOCUMENT_SESSION_CHECKPOINTS_STORE,
  DOCUMENT_SESSION_PENDING_UPDATES_STORE,
  DOCUMENT_SESSION_REPLICAS_STORE,
  openDocumentSessionDb,
} from './documentSessionDb'
import {
  normalizeDocumentSessionClientUpdate,
  normalizeDocumentSessionLease,
  normalizeDocumentSessionSheetChunkId,
  normalizeProjectionDescriptor,
  normalizeSheetChunkDescriptor,
} from '../protocol/documentSessionProtocol'

export type DocumentSessionReplicaCacheRecordV1 = {
  version: 1
  workbook_id: string
  session_id: string
  snapshot_seq: number
  snapshot_payload_b64?: string | null
  materialized_root_payload_b64?: string | null
  materialized_chunk_payloads_b64?: Record<string, string>
  head_op_seq: number
  chunk_descriptors: SheetChunkDescriptor[]
  projection_descriptors: DocumentProjectionDescriptor[]
  lease: DocumentSessionLease | null
  updated_at_ms: number
}

export type DocumentSessionPendingUpdateRecordV1 = DocumentSessionClientUpdate & {
  version: 1
  storage_key: string
  workbook_id: string
  updated_at_ms: number
}

const nowMs = (): number => Date.now()

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value && typeof value === 'object' && !Array.isArray(value))

const normalizeSeq = (value: unknown): number => {
  const next = Number(value)
  if (!Number.isFinite(next) || next < 0) return 0
  return Math.floor(next)
}

const normalizeStoredChunkPayloads = (value: unknown): Record<string, string> => {
  if (!isRecord(value)) return {}
  return Object.fromEntries(
    Object.entries(value)
      .map(([key, entry]) => [normalizeDocumentSessionSheetChunkId(key) || '', String(entry || '').trim()])
      .filter(([key, entry]) => key && entry),
  )
}

const normalizeStoredCheckpoint = (
  value: unknown,
  workbookId: string,
): DocumentSessionCheckpoint | null => {
  if (!isRecord(value)) return null
  const rowWorkbookId = String(value.workbook_id || '').trim()
  const sessionId = String(value.session_id || '').trim()
  if (rowWorkbookId !== workbookId || !sessionId) return null
  return {
    workbook_id: workbookId,
    session_id: sessionId,
    snapshot_seq: normalizeSeq(value.snapshot_seq),
    last_op_seq: normalizeSeq(value.last_op_seq),
  }
}

const normalizeStoredLease = (
  value: unknown,
  nowMsValue: number,
): DocumentSessionLease | null => {
  if (value == null) return null
  const lease = normalizeDocumentSessionLease(value)
  if (!lease.session_id || !lease.resume_token || lease.expires_at_epoch_ms <= nowMsValue) return null
  return lease
}

const normalizeStoredReplica = (
  value: unknown,
  workbookId: string,
  nowMsValue: number,
): DocumentSessionReplicaCacheRecordV1 | null => {
  if (!isRecord(value)) return null
  const rowWorkbookId = String(value.workbook_id || '').trim()
  const sessionId = String(value.session_id || '').trim()
  if (rowWorkbookId !== workbookId || !sessionId) return null
  const lease = normalizeStoredLease(value.lease, nowMsValue)
  return {
    version: 1,
    workbook_id: workbookId,
    session_id: sessionId,
    snapshot_seq: normalizeSeq(value.snapshot_seq),
    snapshot_payload_b64: value.snapshot_payload_b64 ? String(value.snapshot_payload_b64).trim() : null,
    materialized_root_payload_b64: value.materialized_root_payload_b64
      ? String(value.materialized_root_payload_b64).trim()
      : null,
    materialized_chunk_payloads_b64: normalizeStoredChunkPayloads(value.materialized_chunk_payloads_b64),
    head_op_seq: normalizeSeq(value.head_op_seq),
    chunk_descriptors: Array.isArray(value.chunk_descriptors)
      ? value.chunk_descriptors.map(normalizeSheetChunkDescriptor).filter((entry): entry is SheetChunkDescriptor => Boolean(entry))
      : [],
    projection_descriptors: Array.isArray(value.projection_descriptors)
      ? value.projection_descriptors.map(normalizeProjectionDescriptor).filter((entry): entry is DocumentProjectionDescriptor => Boolean(entry))
      : [],
    lease,
    updated_at_ms: normalizeSeq(value.updated_at_ms),
  }
}

export class DocumentSessionStore {
  private readonly openDb: typeof openDocumentSessionDb
  private readonly getNowMs: typeof nowMs

  constructor(openDb: typeof openDocumentSessionDb = openDocumentSessionDb, getNowMs: typeof nowMs = nowMs) {
    this.openDb = openDb
    this.getNowMs = getNowMs
  }

  async getCheckpoint(workbookIdRaw: string): Promise<DocumentSessionCheckpoint | null> {
    const workbookId = String(workbookIdRaw || '').trim()
    if (!workbookId) return null
    const row = await (await this.openDb()).get(DOCUMENT_SESSION_CHECKPOINTS_STORE, workbookId)
    return normalizeStoredCheckpoint(row, workbookId)
  }

  async putCheckpoint(checkpoint: DocumentSessionCheckpoint): Promise<void> {
    const workbookId = String(checkpoint?.workbook_id || '').trim()
    const sessionId = String(checkpoint?.session_id || '').trim()
    if (!workbookId || !sessionId) return
    await (await this.openDb()).put(DOCUMENT_SESSION_CHECKPOINTS_STORE, {
      workbook_id: workbookId,
      session_id: sessionId,
      snapshot_seq: normalizeSeq(checkpoint?.snapshot_seq),
      last_op_seq: normalizeSeq(checkpoint?.last_op_seq),
      updated_at_ms: this.getNowMs(),
    })
  }

  async getReplica(workbookIdRaw: string): Promise<DocumentSessionReplicaCacheRecordV1 | null> {
    const workbookId = String(workbookIdRaw || '').trim()
    if (!workbookId) return null
    const row = await (await this.openDb()).get(DOCUMENT_SESSION_REPLICAS_STORE, workbookId)
    return normalizeStoredReplica(row, workbookId, this.getNowMs())
  }

  async putReplica(replica: {
    workbook_id: string
    session_id: string
    snapshot_seq: number
    snapshot_payload_b64?: string | null
    materialized_root_payload_b64?: string | null
    materialized_chunk_payloads_b64?: Record<string, string>
    head_op_seq: number
    chunk_descriptors: SheetChunkDescriptor[]
    projection_descriptors: DocumentProjectionDescriptor[]
    lease?: DocumentSessionLease | null
  }): Promise<void> {
    const workbookId = String(replica?.workbook_id || '').trim()
    const sessionId = String(replica?.session_id || '').trim()
    if (!workbookId || !sessionId) return
    const lease = normalizeStoredLease(replica.lease, this.getNowMs())
    await (await this.openDb()).put(DOCUMENT_SESSION_REPLICAS_STORE, {
      version: 1,
      workbook_id: workbookId,
      session_id: sessionId,
      snapshot_seq: normalizeSeq(replica?.snapshot_seq),
      snapshot_payload_b64: replica?.snapshot_payload_b64 ? String(replica.snapshot_payload_b64).trim() : null,
      materialized_root_payload_b64: replica?.materialized_root_payload_b64
        ? String(replica.materialized_root_payload_b64).trim()
        : null,
      materialized_chunk_payloads_b64: normalizeStoredChunkPayloads(replica?.materialized_chunk_payloads_b64),
      head_op_seq: normalizeSeq(replica?.head_op_seq),
      chunk_descriptors: Array.isArray(replica?.chunk_descriptors)
        ? replica.chunk_descriptors.map(normalizeSheetChunkDescriptor).filter((entry): entry is SheetChunkDescriptor => Boolean(entry))
        : [],
      projection_descriptors: Array.isArray(replica?.projection_descriptors)
        ? replica.projection_descriptors.map(normalizeProjectionDescriptor).filter((entry): entry is DocumentProjectionDescriptor => Boolean(entry))
        : [],
      lease,
      updated_at_ms: this.getNowMs(),
    } satisfies DocumentSessionReplicaCacheRecordV1)
  }

  async listPendingUpdates(workbookIdRaw: string): Promise<DocumentSessionPendingUpdateRecordV1[]> {
    const workbookId = String(workbookIdRaw || '').trim()
    if (!workbookId) return []
    const rows = await (await this.openDb()).getAllFromIndex(
      DOCUMENT_SESSION_PENDING_UPDATES_STORE,
      'by_workbook_id',
      workbookId,
    )
    return rows
      .map((row): DocumentSessionPendingUpdateRecordV1 | null => {
        const update = normalizeDocumentSessionClientUpdate(row)
        if (!update) return null
        const record = isRecord(row) ? row : {}
        return {
          version: 1,
          storage_key: String(record.storage_key || `${workbookId}::${update.client_update_id}`).trim(),
          workbook_id: workbookId,
          ...update,
          chunk_id: update.chunk_id ?? null,
          updated_at_ms: Math.max(0, Math.floor(Number(record.updated_at_ms) || 0)),
        }
      })
      .filter((row): row is DocumentSessionPendingUpdateRecordV1 => Boolean(row))
      .sort((a, b) => Number(a.updated_at_ms || 0) - Number(b.updated_at_ms || 0))
  }

  async putPendingUpdate(workbookIdRaw: string, update: DocumentSessionClientUpdate): Promise<void> {
    const workbookId = String(workbookIdRaw || '').trim()
    const normalized = normalizeDocumentSessionClientUpdate(update)
    if (!workbookId || !normalized) return
    await (await this.openDb()).put(DOCUMENT_SESSION_PENDING_UPDATES_STORE, {
      version: 1,
      storage_key: `${workbookId}::${normalized.client_update_id}`,
      workbook_id: workbookId,
      client_id: normalized.client_id,
      client_update_id: normalized.client_update_id,
      doc_scope: normalized.doc_scope,
      chunk_id: normalized.chunk_id ?? null,
      update_payload_b64: normalized.update_payload_b64,
      updated_at_ms: this.getNowMs(),
    } satisfies DocumentSessionPendingUpdateRecordV1)
  }

  async deletePendingUpdates(workbookIdRaw: string, clientUpdateIdsRaw: string[]): Promise<void> {
    const workbookId = String(workbookIdRaw || '').trim()
    if (!workbookId) return
    const clientUpdateIds = (Array.isArray(clientUpdateIdsRaw) ? clientUpdateIdsRaw : [])
      .map((value) => String(value || '').trim())
      .filter(Boolean)
    if (!clientUpdateIds.length) return
    const db = await this.openDb()
    await Promise.all(clientUpdateIds.map(async (clientUpdateId) => {
      await db.delete(DOCUMENT_SESSION_PENDING_UPDATES_STORE, `${workbookId}::${clientUpdateId}`)
    }))
  }
}
